import clsx from "clsx";
import React, { useContext, useEffect } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  useMediaQuery,
  Box,
  Theme,
  IconButton,
  ListItemIcon,
  Typography,
  Button,
} from "@mui/material";
import { darken, useTheme } from "@mui/material/styles";
import { InfoOutlined } from "@mui/icons-material";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";

import makeStyles from "@mui/styles/makeStyles";

import { LocalStorageKeys, TeamRole, UserRole } from "../../constants";
import { polyfillCSS } from "../../utils/css";
import { getUserRole } from "../../utils/user";
import { GroupNavItem } from "../groups/GroupNavItem";
import { Link } from "../link/Link";

import { AppLogo } from "./AppLogo";
import { CurrentUserBox } from "./CurrentUserBox";
import { Toolbar } from "./AppBar";
import { menuItems } from "./constants";
import {
  COACH_CLIENT_PORTAL_ROUTE,
  COACH_LIBRARY_ROUTE,
  COACH_PROGRAMS_PUBLISHED_ROUTE,
  COACH_TEAM_ROUTE,
  GROUPS_ROUTE,
  HOME_ROUTE,
} from "../../routes/routes";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useCurrentUserRole } from "../../hooks/useCurrentUser";
import { useLocation } from "react-router-dom";
import { isDrawerItemSelected } from "./common";
import { useDrawer } from "../../hooks/useDrawer";
import { ChevronsLeftIcon } from "lucide-react";
import { ChevronsRightIcon } from "lucide-react";
import MinimizedDrawerContext from "../../contexts/MinimizedDrawerContext";
import { useMediaMobile } from "../../hooks/useMediaMobile";
import { LibraryNavItem } from "../exercise-library/LibraryNavItem";
import { JourneyLauncher } from "./JourneyLauncher";
import { JourneyRouteManager } from "../../routes/admin/onboardingTemplate/JourneyRouteManager";
import { JourneyRunContext } from "../../contexts/JourneyRunContext";
import { DrawerAnnouncement } from "./DrawerAnnouncement";
import { useQuery } from "@tanstack/react-query";
import AnnouncementsService from "../../services/AnnouncementsService";

export const DRAWER_ANNOUNCEMENT_QUERY_KEY = "announcement";

export const sharedLabelAnimationSx = (
  minimized: boolean,
  { collapseDur = 100, collapseDel = 0, expandDur = 300, expandDel = 200 } = {},
) => ({
  visibility: minimized ? "hidden" : "visible",
  opacity: minimized ? 0 : 1,
  transform: minimized ? "translateX(-8px)" : "translateX(0)",
  transition: `
    opacity ${minimized ? collapseDur : expandDur}ms ease ${minimized ? collapseDel : expandDel}ms,
    transform ${minimized ? collapseDur : expandDur}ms ease ${minimized ? collapseDel : expandDel}ms,
    visibility 0ms linear ${minimized ? collapseDur : 0}ms
  `,
});

const useStyles = (minimizedDrawer) =>
  makeStyles(
    ({ palette, spacing, breakpoints, drawer, transitions, zIndex }) => ({
      "@global": {
        "#root": {
          [breakpoints.up("llg")]: {
            paddingLeft: polyfillCSS(
              `calc(${minimizedDrawer ? drawer.minimizedWidth : drawer.width}px + var(--safe-area-inset-left))`,
            ),
            transition: transitions.create("padding-left", {
              easing: transitions.easing.sharp,
              duration: transitions.duration.enteringScreen,
            }),
          },
        },
      },

      root: {
        width: polyfillCSS(
          `calc(${drawer.width}px + var(--safe-area-inset-left))`,
        ),
        flexShrink: 0,

        [breakpoints.up("md")]: {
          display: "none",
        },

        [breakpoints.up("llg")]: {
          display: "block",
        },
      },

      paper: {
        width: polyfillCSS(
          `calc(${drawer.width}px + var(--safe-area-inset-left))`,
        ),
        borderRight: "1px solid",
        borderRightColor: palette.grey[300],

        paddingLeft: polyfillCSS("var(--safe-area-inset-left)"),
        paddingTop: polyfillCSS(
          `calc(var(--safe-area-inset-top) + ${spacing(1)})`,
        ),
      },

      toolbar: {
        paddingInline: spacing(minimizedDrawer ? 1 : 2.5),
        paddingTop: spacing(5.5),
        paddingBottom: spacing(2),
        transition: transitions.create("padding", {
          easing: transitions.easing.sharp,
          duration: transitions.duration.enteringScreen,
        }),
        display: "flex",
        alignContent: "center",
        minHeight: "125px",

        [breakpoints.down("md")]: {
          "@media (orientation: landscape)": {
            display: "none",
          },
        },
      },

      logo: {
        height: "auto",
        width: minimizedDrawer ? "45px" : "60px",

        transition: transitions.create(["height", "width"], {
          easing: transitions.easing.sharp,
          duration: transitions.duration.enteringScreen,
        }),
      },

      title: {
        paddingTop: spacing(2),
        paddingLeft: spacing(2),
      },

      nav: {
        flexGrow: 1,
        overflowY: "auto",
        overflowX: "hidden",
      },

      navItem: {
        minHeight: 50,
        position: "sticky",
        top: 0,
        background: "white",
        zIndex: zIndex.mobileStepper,

        "&:hover": {
          background: darken(palette.common.white, 0.05),
        },
      },

      icon: {
        color: palette.text.secondary,

        "&$active": {
          color: palette.primary.main,
        },
      },

      itemText: {
        fontSize: 19,
        color: palette.text.secondary,

        "&$active": {
          color: palette.primary.main,
        },
      },

      unreadCount: {
        width: 18,
        height: 18,
        borderRadius: "50%",
        backgroundColor: palette.primary.main,
      },

      tutorialContainer: {
        display: "flex",
        flexDirection: minimizedDrawer ? "column" : "row",
        alignItems: minimizedDrawer ? "stretch" : "center",
        justifyContent: "center",
        width: "100%",
        paddingTop: spacing(1),
        paddingBottom: spacing(1),
        paddingLeft: minimizedDrawer ? 0 : spacing(4),
        paddingRight: minimizedDrawer ? 0 : spacing(4),
        gap: spacing(1),

        [breakpoints.up("sm")]: {
          height: minimizedDrawer ? "auto" : spacing(6),
          minHeight: spacing(6),
        },
      },

      tutorialButton: {
        color: palette.text.secondary,
        textTransform: "none",
        minWidth: 0,

        ...(minimizedDrawer
          ? {
              width: "100%",
              justifyContent: "center",
              paddingBlock: spacing(1),
              paddingInline: 0,
              "& .MuiButton-startIcon": {
                margin: 0,
              },
              "& .MuiSvgIcon-root": {
                fontSize: "1.25rem",
              },
            }
          : {
              "& .MuiButton-startIcon": {
                marginRight: spacing(0.25),
                marginLeft: 0,
              },
            }),

        "&:hover": {
          background: darken(palette.common.white, 0.05),
        },
      },

      tutorialText: {
        color: palette.text.secondary,
      },

      active: {},
    }),
  );

export const APP_DRAWER_ICON_WIDTH = 40;

function AppDrawer(props) {
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));
  const { className, ...other } = props;
  const { breakpoints } = useTheme();
  const location = useLocation();
  const mediaQuery = `(min-width:${breakpoints.values.md}px)`;
  const mdUp = useMediaQuery(mediaQuery);
  const [drawerOpen, setDrawerOpen] = useDrawer();
  const mode = useCurrentUserRole();
  const me = useCurrentUser();
  const teamRole = me?.teamRole;

  const theme = useTheme();

  const { minimizedDrawer, setMinimizedDrawer } = useContext(
    MinimizedDrawerContext,
  );
  const isMobile = useMediaMobile();
  const showMinimizedDrawbar = minimizedDrawer && !isMobile;
  const s = useStyles(showMinimizedDrawbar)();

  const handleMinimizeDrawerClick = () => {
    setMinimizedDrawer((v) => {
      localStorage.setItem(
        LocalStorageKeys.IS_DRAWER_MINIMIZED,
        !v ? "true" : "false",
      );
      return !v;
    });
  };

  const { data: announcement } = useQuery({
    queryKey: [DRAWER_ANNOUNCEMENT_QUERY_KEY],
    queryFn: AnnouncementsService.getCurrentAnnouncement,
    staleTime: 3_600_000, // 1 hour
    enabled: mode === UserRole.COACH,
  });

  const handleUserMenuClose = React.useCallback((closeAppDrawer: boolean) => {
    if (closeAppDrawer) {
      setDrawerOpen(false);
    }
  }, []);

  const { setIsRunning, setIsForced } = useContext(JourneyRunContext);

  const handleJourneyButtonClick = React.useCallback(() => {
    if (!mdUp) setDrawerOpen(false);
    setIsForced(true);
    setIsRunning(true);
  }, [mdUp, setDrawerOpen]);

  const items = React.useMemo(() => {
    switch (mode) {
      case UserRole.CLIENT:
        return menuItems.client;
      case UserRole.COACH:
        return menuItems.coach.filter(
          (item) => !item.role || item.role === teamRole,
        );
      default:
        throw new Error(`Unknown menu item mode ${mode}`);
    }
  }, [mode, teamRole]);

  return (
    <>
      <Drawer
        className={clsx(s.root, className)}
        {...other}
        classes={{ paper: s.paper }}
        open={mdUp ? true : drawerOpen}
        variant={mdUp ? "permanent" : "temporary"}
        anchor="left"
        PaperProps={{
          style: {
            width: showMinimizedDrawbar
              ? theme.drawer.minimizedWidth
              : theme.drawer.width,
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflow: "unset",
          },
        }}
      >
        {!isMobile && (
          <IconButton
            id="app-drawer-minimize-icon-button"
            onClick={handleMinimizeDrawerClick}
            sx={{
              position: "absolute",
              top: 60,
              right: -18,
              p: 0.5,
              zIndex: theme.zIndex.modal,
              background: theme.palette.common.white,
              ":hover": {
                background: darken(theme.palette.common.white, 0.05),
              },
            }}
          >
            {minimizedDrawer ? <ChevronsRightIcon /> : <ChevronsLeftIcon />}
          </IconButton>
        )}
        <Toolbar className={s.toolbar}>
          <Link
            href={
              mode === UserRole.CLIENT
                ? HOME_ROUTE
                : COACH_PROGRAMS_PUBLISHED_ROUTE
            }
            underline="none"
            children={
              <AppLogo
                className={clsx(s.logo)}
                size={showMinimizedDrawbar ? 45 : 60}
                width={showMinimizedDrawbar ? 45 : 60}
                height={showMinimizedDrawbar ? 45 : 60}
              />
            }
          />
        </Toolbar>
        <List className={s.nav}>
          {items.map(({ text, link, Icon, type }) =>
            type === "divider" ? (
              <Divider
                orientation="horizontal"
                sx={{ mx: 2, my: 0.5, borderBottomWidth: 1 }}
              />
            ) : link.includes(GROUPS_ROUTE) ? (
              <GroupNavItem
                key={link}
                minimized={showMinimizedDrawbar}
                setDrawerMinimized={setMinimizedDrawer}
              />
            ) : link.includes(COACH_LIBRARY_ROUTE) ? (
              <LibraryNavItem
                key={link}
                minimized={showMinimizedDrawbar}
                setDrawerMinimized={setMinimizedDrawer}
              />
            ) : (
              <ListItemButton
                key={link}
                className={s.navItem}
                component={Link}
                href={link}
                target={link.includes("marketing") && "_blank"}
                onClick={() => {
                  setDrawerOpen(false);
                }}
                underline="none"
                sx={{
                  ...(!showMinimizedDrawbar && {
                    p: 0,
                    paddingLeft: theme.spacing(4),
                  }),
                  transition: theme.transitions.create(["padding-left"], {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                  }),
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: showMinimizedDrawbar ? 30 : APP_DRAWER_ICON_WIDTH,
                  }}
                >
                  <Icon
                    size={24}
                    className={clsx(
                      s.icon,
                      isDrawerItemSelected(link, mode) && s.active,
                    )}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={text}
                  primaryTypographyProps={{
                    className: clsx(
                      s.itemText,
                      isDrawerItemSelected(link, mode) && s.active,
                    ),
                    variant: "h5",
                  }}
                  sx={{
                    ...sharedLabelAnimationSx(showMinimizedDrawbar),
                  }}
                />
              </ListItemButton>
            ),
          )}
        </List>

        {!showMinimizedDrawbar && announcement?.isActive && (
          <DrawerAnnouncement
            content={JSON.parse(announcement?.content || "[]")}
            buttonText={announcement?.buttonText}
            link={announcement?.buttonLink}
            animation={mdUp}
          />
        )}

        <JourneyLauncher
          wrapperClassName={s.tutorialContainer}
          buttonClassName={s.tutorialButton}
          fullWidth={showMinimizedDrawbar}
          minimized={showMinimizedDrawbar}
          onStart={handleJourneyButtonClick}
        />

        <Divider />
        <CurrentUserBox
          minimized={showMinimizedDrawbar}
          onClose={handleUserMenuClose}
        />
      </Drawer>
    </>
  );
}

export default AppDrawer;
